/**
 * Web/booking CRM entity types: web visits, visitors, forms, booking types,
 * bookings. Split out of entityTypeRegistry.ts — see that file for the merge
 * point.
 */

import { EventNameTemplateEditor } from '../../components/eventNameTemplate/EventNameTemplateEditor';
import { DurationField } from '../../create/configs/BookingTypeDurationField';
import { getDisplayName } from '../../utils/getDisplayName';
import { BookingAnswersRows } from '../sections/booking-answers-rows';
import { BookingLinkSection } from '../sections/booking-link-section';
import { EmbedCodeSection } from '../sections/embed-code-section';
import { ALL_EVENT_TYPES } from '../timeline/timelineFilterDefaults';
import type { EntityTypeConfig } from './entityTypeRegistry';
import type { RelatedEntitySectionConfig } from './relatedSections';

/**
 * "Meeting": the calendar meeting linked to a booking, resolved server-side via
 * `associated_with_entity_id` (the `ahoy_booking_to_meeting` association). Uses
 * the detail-oriented `meetingDetailCard` renderer — a booking's single linked
 * meeting warrants the status badge, attendee count, and "Join meeting" link
 * that the compact list-style `meetingCard` (shared with "Upcoming meetings")
 * omits for space. Unlike upcoming meetings, there is no future-only filter —
 * the linked meeting is shown whether it is upcoming or past.
 */
const BOOKING_MEETING_SECTION: RelatedEntitySectionConfig = {
  key: 'booking-meeting',
  title: 'Meeting',
  entityType: 'ahoy_meeting',
  source: { kind: 'association' },
  sort: { property: 'ahoy_start_time', order: 'desc' },
  limit: 5,
  properties: [
    'ahoy_title',
    'ahoy_start_time',
    'ahoy_end_time',
    'ahoy_meeting_link',
    'ahoy_status',
    'ahoy_color',
    'ahoy_invitations',
    'ahoy_origin',
  ],
  includeAssociations: {
    ahoy_meeting_to_company: { properties: ['ahoy_name'] },
  },
  card: 'meetingDetailCard',
  emptyState: 'No linked meeting yet',
  errorMessage: "We couldn't load the linked meeting.",
  itemNounPlural: 'meetings',
  itemNounSingular: 'meeting',
};

/** Web/booking CRM entity types: web visits, visitors, forms, booking types, bookings. */
export const WEB_ENTITY_TYPES: Record<string, EntityTypeConfig> = {
  ahoy_web_visit: {
    displayName: 'Visit',
    pluralDisplayName: 'Visits',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
    getDisplayName,
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {},
    },
    routes: {
      list: '/:orgId/crm/ahoy_web_visit',
      detail: '/:orgId/crm/ahoy_web_visit/:id',
    },
  },

  ahoy_web_visitor: {
    displayName: 'Visitor',
    pluralDisplayName: 'Visitors',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
    getDisplayName,
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {},
    },
  },

  ahoy_web_form: {
    displayName: 'Web Form',
    pluralDisplayName: 'Web Forms',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
    getDisplayName,
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {},
    },
    routes: {
      list: '/:orgId/crm/ahoy_web_form',
      detail: '/:orgId/crm/ahoy_web_form/:id',
    },
  },

  ahoy_booking_type: {
    displayName: 'Booking Link',
    pluralDisplayName: 'Booking Links',
    color: 'blue',
    bannerGradient: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
    getDisplayName,
    routes: {
      list: '/:orgId/crm/booking-types',
      detail: '/:orgId/crm/ahoy_booking_type/:id',
    },
    highlights: [
      {
        kind: 'association_count',
        label: 'Bookings',
        associationType: 'ahoy_booking_to_booking_type',
      },
      {
        kind: 'entity_count',
        label: 'Bookings (30d)',
        filter: {
          entityType: 'ahoy_booking',
          associationType: 'ahoy_booking_to_booking_type',
          sinceDays: 30,
        },
      },
      {
        kind: 'property',
        label: 'Duration',
        propertyName: 'ahoy_duration_minutes',
      },
      { kind: 'property', label: 'Active', propertyName: 'ahoy_is_active' },
    ],
    tabs: ['overview', 'activity', 'emails', 'notes', 'associations'],
    // Show all event types in the timeline — booking links don't have a
    // communications-centric workflow, so lifecycle/property/association
    // events are more relevant than the default emails-only filter.
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {
        eventTypes: ALL_EVENT_TYPES,
      },
    },
    // Slug fields are edited through the BookingLinkSection "Edit URL slug"
    // dialog. ahoy_is_active is toggled via the "Archive/Activate" record
    // action in the header dropdown — not a form field.
    hiddenProperties: ['ahoy_slug', 'ahoy_global_slug', 'ahoy_is_active'],
    advancedFields: ['ahoy_event_name_template'],
    detailFieldOrder: [
      // Basics
      { type: 'property', key: 'ahoy_name' },
      { type: 'property', key: 'ahoy_description' },
      { type: 'property', key: 'ahoy_event_name_template' },
      { type: 'property', key: 'ahoy_scheduling_type' },
      { type: 'property', key: 'ahoy_timezone' },
      // Duration & availability
      { type: 'property', key: 'ahoy_duration_minutes' },
      { type: 'property', key: 'ahoy_buffer_before_minutes' },
      { type: 'property', key: 'ahoy_buffer_after_minutes' },
      { type: 'property', key: 'ahoy_min_notice_hours' },
      { type: 'property', key: 'ahoy_max_days_ahead' },
      // Location
      { type: 'property', key: 'ahoy_location_type' },
      { type: 'property', key: 'ahoy_custom_location' },
      // Working hours (dedicated editor via WorkingHoursInput)
      { type: 'property', key: 'ahoy_working_hours' },
      // Custom questions — last configurable block, just before the Advanced
      // group, mirroring the create form's field order (working hours →
      // custom questions → advanced).
      { type: 'property', key: 'ahoy_custom_questions' },
      // Timestamps
      { type: 'timestamp', key: 'ahoy_created_at' },
      { type: 'timestamp', key: 'ahoy_updated_at' },
    ],
    customPropertyEditors: {
      ahoy_duration_minutes: DurationField,
      ahoy_event_name_template: EventNameTemplateEditor,
    },
    // Booking URLs render above the properties section; no summary card needed.
    typeSpecificPosition: 'before-overview',
    components: {
      summary: null,
      typeSpecificSections: [
        { kind: 'custom', key: 'booking-link', component: BookingLinkSection },
        { kind: 'custom', key: 'embed-code', component: EmbedCodeSection },
      ],
    },
  },

  ahoy_booking: {
    displayName: 'Booking',
    pluralDisplayName: 'Bookings',
    color: 'green',
    bannerGradient: 'linear-gradient(135deg, #10B981 0%, #14B8A6 100%)',
    getDisplayName,
    routes: {
      list: '/:orgId/crm/bookings',
      detail: '/:orgId/crm/ahoy_booking/:id',
    },
    customPropertyReadRenderers: {
      ahoy_custom_answers: BookingAnswersRows,
    },
    components: {
      typeSpecificSections: [
        { kind: 'section', config: BOOKING_MEETING_SECTION },
      ],
    },
  },
};
